import type { GroupedPoiByBuildingId, PoiItem } from '@/types/map';
import { useConfigStore } from '@/stores';
import type { DabeeoMap, DabeeoMapData } from 'dabeeomaps';
import type { IPoiOption } from 'dabeeomaps/dist/src/model/map/IPoiOption';
import { useCallback, useMemo } from 'react';

export const usePoi = (currentMap?: DabeeoMap, mapData?: DabeeoMapData) => {
    const storeBuildings = useConfigStore((state) => state.config?.server?.store?.buildings);

    const setPoi = useCallback(
        (options: IPoiOption) => {
            if (!currentMap) return;
            currentMap.pois.set(options);
        },
        [currentMap]
    );

    const resetPoi = useCallback(
        (id?: string | string[]) => {
            if (!currentMap) return;
            currentMap.pois.reset(id);
        },
        [currentMap]
    );

    const mapPoiList = useMemo(() => {
        const pois: PoiItem[] = mapData?.dataPoi?.getPois() as PoiItem[];
        if (!Array.isArray(pois) || !pois?.length) {
            return [];
        }

        return pois;
    }, [mapData?.dataPoi]);

    const convertFloorsToKoreanMap = useMemo(() => {
        const floors = mapData?.dataFloor?.getFloors() || [];
        return floors.reduce<{ [key: string]: string | null }>((acc, floor) => {
            const korean = floor.name.find((n) => n.lang === 'ko');
            acc[floor.id] = korean?.text ?? null;
            return acc;
        }, {});
    }, [mapData?.dataFloor]);

    const floorIdToStudioBuildingIdMap = useMemo(() => {
        const map = new Map<string, string>();
        const buildings = mapData?.dataBuilding?.getBuildings?.() ?? [];

        for (const building of buildings) {
            const storeBuilding = storeBuildings?.find((building) => building.studioBuildingId === building.id);
            const studioBuildingId = storeBuilding?.studioBuildingId ?? building.id;

            for (const floor of building.floors ?? []) {
                map.set(floor.id, studioBuildingId);
            }
        }

        for (const building of storeBuildings ?? []) {
            for (const floor of building.floors ?? []) {
                map.set(floor.floorCode, building.studioBuildingId);
                map.set(floor.id, building.studioBuildingId);
            }
        }

        return map;
    }, [mapData?.dataBuilding, storeBuildings]);

    const groupedPoiByBuildingId = useMemo(() => {
        if (!Array.isArray(mapPoiList) || mapPoiList.length === 0) {
            return new Map<string, Map<string, Map<string, PoiItem[]>>>() as GroupedPoiByBuildingId;
        }

        const poiMap: GroupedPoiByBuildingId = new Map();

        for (const poi of mapPoiList) {
            const buildingId = floorIdToStudioBuildingIdMap.get(poi.floorId);
            if (!buildingId) {
                continue;
            }

            const poiFloorName =
                convertFloorsToKoreanMap[poi.floorId] ||
                storeBuildings
                    ?.flatMap((building) => building.floors)
                    .find((floor) => floor.floorCode === poi.floorId || floor.id === poi.floorId)?.name?.['ko'] ||
                '';

            if (!poiFloorName) {
                continue;
            }

            let buildingMap = poiMap.get(buildingId);
            if (!buildingMap) {
                buildingMap = new Map();
                poiMap.set(buildingId, buildingMap);
            }

            let categoryMap = buildingMap.get(poiFloorName);
            if (!categoryMap) {
                categoryMap = new Map();
                buildingMap.set(poiFloorName, categoryMap);
            }

            if (!poi?.categoryCode) {
                continue;
            }

            const categories = Array.isArray(poi.categoryCode) ? poi.categoryCode : [poi.categoryCode];

            for (const categoryCode of categories) {
                let poiList = categoryMap.get(categoryCode);
                if (!poiList) {
                    poiList = [];
                    categoryMap.set(categoryCode, poiList);
                }
                poiList.push(poi);
            }
        }

        return poiMap;
    }, [mapPoiList, convertFloorsToKoreanMap, floorIdToStudioBuildingIdMap]);

    return {
        setPoi,
        resetPoi,
        mapPoiList,
        groupedPoiByBuildingId,
    };
};
